import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils.ts";

export const badgeVariantsConfig = {
  variant: {
    default: "border-transparent bg-primary text-primary-foreground",
    /** Low-contrast brand treatment: light blue-purple ground, dark purple label. */
    brand: "border-transparent bg-brand-100 text-brand-800",
    secondary: "border-transparent bg-tertiary text-tertiary-foreground",
    tertiary: "border-transparent bg-neutral-100 text-neutral-700", // TODO: sync with design on naming/intention, if we keep it, we should have design tokens
    accent: "border-transparent bg-accent text-accent-foreground",
    destructive: "border-transparent bg-destructive text-destructive-foreground",
    outline: "text-foreground",
  },
  size: {
    xs: "font-light text-2xs px-1 py-0.5",
    sm: "font-medium text-xs px-2 py-1",
    md: "font-medium text-sm px-2 py-0.5",
  },
} as const;

export const badgeDefaultVariants = {
  variant: "default",
  size: "md",
} as const satisfies {
  [K in keyof typeof badgeVariantsConfig]: keyof (typeof badgeVariantsConfig)[K];
};

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border whitespace-nowrap transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: badgeVariantsConfig,
    defaultVariants: badgeDefaultVariants,
  },
);

export type BadgeProps = React.PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof badgeVariants>
>;

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, children, ...props }: BadgeProps, ref) => {
    return (
      <div className={cn(badgeVariants({ variant, size }), className)} {...props} ref={ref}>
        {children}
      </div>
    );
  },
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
